import React, { useState, useEffect, useRef } from 'react';

const setCookie = (name, value, days = 365) => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/;SameSite=Strict`;
};

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

export default function App() {
  const [gameState, setGameState] = useState('HOME');
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(() => {
    return parseInt(getCookie('flappy_ears_highscore') || '0', 10);
  });
  
  const [buttonPressedState, setButtonPressedState] = useState(false);
  const [luxuriousCinemaActive, setLuxuriousCinemaActive] = useState(false);
  
  const [shatteredBlocks, setShatteredBlocks] = useState([]);
  const [isShatteredActive, setIsShatteredActive] = useState(false);

  const canvasRef = useRef(null);
  const requestRef = useRef(null);
  const audioCtxRef = useRef(null);
  const musicIntervalRef = useRef(null);

  const imgIdle = useRef(new Image());
  const imgAction = useRef(new Image());
  const spritesLoaded = useRef(false);

  const visualSize = 72; 
  const hitboxSize = 28; 

  const stateRef = useRef({
    gameState: 'HOME',
    bunny: { x: 50, y: 180, vy: 0, width: hitboxSize, height: hitboxSize },
    charge: 0,
    isCharging: false,
    overshot: false,
    pipes: [],
    frameCounter: 0,
    score: 0,
    flashTimer: 0,
    hasSpawnedGoal: false,
    goalX: null
  });

  useEffect(() => {
    document.title = "Flappy Ears - The Ultimate Retro Arcade Jump Game";
    
    const metaDescription = document.querySelector('meta[name="description"]') || document.createElement('meta');
    metaDescription.name = "description";
    metaDescription.content = "Charge your jumps, timing your rhythm perfectly to dodge obstacles in Flappy Ears. Built with minimalist black and white chiptune aesthetics for retro arcade lovers.";
    if (!metaDescription.parentNode) document.head.appendChild(metaDescription);

    const metaKeywords = document.querySelector('meta[name="keywords"]') || document.createElement('meta');
    metaKeywords.name = "keywords";
    metaKeywords.content = "flappy ears, react game, arcade game, retro game, 8bit chiptune, bunny animation, HTML5 canvas game";
    if (!metaKeywords.parentNode) document.head.appendChild(metaKeywords);
  }, []);

  useEffect(() => {
    imgIdle.current.src = '/sprite_0001.png';
    imgAction.current.src = '/sprite_0002.png';
    
    let loadedCount = 0;
    const checkLoaded = () => {
      loadedCount++;
      if (loadedCount === 2) spritesLoaded.current = true;
    };
    imgIdle.current.onload = checkLoaded;
    imgAction.current.onload = checkLoaded;
  }, []);

  useEffect(() => {
    stateRef.current.gameState = gameState;
    
    if ((gameState === 'HOME' || gameState === 'PLAYING') && !luxuriousCinemaActive) {
      startHeavySawMusic();
    } else {
      stopMusic();
    }
    
    return () => stopMusic();
  }, [gameState, luxuriousCinemaActive]);

  const initAudio = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
  };

  const playSound = (type) => {
    initAudio();
    const ctx = audioCtxRef.current;
    if (!ctx) return;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    const now = ctx.currentTime;

    if (type === 'click') {
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(800, now);
      osc.frequency.linearRampToValueAtTime(100, now + 0.08);
      gain.gain.setValueAtTime(0.3, now);
      gain.gain.linearRampToValueAtTime(0.001, now + 0.08);
      osc.start(now);
      osc.stop(now + 0.08);
    } else if (type === 'death') {
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(120, now);
      osc.frequency.linearRampToValueAtTime(10, now + 0.45);
      gain.gain.setValueAtTime(0.3, now);
      gain.gain.linearRampToValueAtTime(0.001, now + 0.45);
      osc.start(now);
      osc.stop(now + 0.45);
    } else if (type === 'win') {
      osc.type = 'square';
      osc.frequency.setValueAtTime(440, now);
      osc.frequency.setValueAtTime(554.37, now + 0.1);
      osc.frequency.setValueAtTime(659.25, now + 0.2);
      osc.frequency.setValueAtTime(880.00, now + 0.3);
      gain.gain.setValueAtTime(0.15, now);
      gain.gain.linearRampToValueAtTime(0.001, now + 0.5);
      osc.start(now);
      osc.stop(now + 0.5);
    } else if (type === 'break') {
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(100, now);
      osc.frequency.exponentialRampToValueAtTime(30, now + 1.5);
      gain.gain.setValueAtTime(0.4, now);
      gain.gain.linearRampToValueAtTime(0.001, now + 1.5);
      osc.start(now);
      osc.stop(now + 1.5);
    }
  };

  const startHeavySawMusic = () => {
    stopMusic();
    initAudio();
    
    let step = 0;
    const notes = [
      130, 0, 180, 130, 0, 180, 130, 130,
      110, 0, 165, 110, 0, 165, 110, 110
    ];

    musicIntervalRef.current = setInterval(() => {
      const ctx = audioCtxRef.current;
      if (!ctx || ctx.state === 'suspended') return;

      const currentFreq = notes[step % notes.length];
      if (currentFreq > 0) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(currentFreq, ctx.currentTime);
        gain.gain.setValueAtTime(0.045, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0.001, ctx.currentTime + 0.14);

        osc.start();
        osc.stop(ctx.currentTime + 0.15);
      }
      step++;
    }, 180);
  };

  const stopMusic = () => {
    if (musicIntervalRef.current) clearInterval(musicIntervalRef.current);
  };

  const handlePlayClick = (e) => {
    if (e) {
      e.stopPropagation();
      if (e.cancelable) e.preventDefault();
    }
    
    if (buttonPressedState || luxuriousCinemaActive) return;
    initAudio();
    if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }

    setButtonPressedState(true);
    playSound('click');

    setTimeout(() => {
      setLuxuriousCinemaActive(true);
      stopMusic();

      const ctx = audioCtxRef.current;
      const now = ctx.currentTime;

      const rumbleOsc = ctx.createOscillator();
      const rumbleGain = ctx.createGain();
      rumbleOsc.type = 'sawtooth';
      rumbleOsc.frequency.setValueAtTime(60, now);
      rumbleOsc.frequency.linearRampToValueAtTime(600, now + 0.7);
      rumbleGain.gain.setValueAtTime(0.2, now);
      rumbleGain.gain.linearRampToValueAtTime(0.001, now + 0.7);
      rumbleOsc.connect(rumbleGain);
      rumbleGain.connect(ctx.destination);
      rumbleOsc.start(now);
      rumbleOsc.stop(now + 0.7);

      setTimeout(() => {
        const state = stateRef.current;
        state.bunny.y = 150;
        state.bunny.vy = 0;
        state.charge = 0;
        state.isCharging = false;
        state.overshot = false;
        state.pipes = [];
        state.score = 0;
        state.flashTimer = 0;
        state.hasSpawnedGoal = false;
        state.goalX = null;
        
        setScore(0);
        setButtonPressedState(false);
        setLuxuriousCinemaActive(false);
        setGameState('PLAYING');
      }, 750);

    }, 100); 
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    canvas.width = 450;
    canvas.height = 550;

    const gravity = 0.35;
    const maxCharge = 14;
    const pipeWidth = 70;
    const pipeGap = 150; 
    const pipeSpeed = 2.2;
    const symbols = ["#", "X", "%", "@", "?", "$", "&", "K", "8", "W"];

    const spawnPipe = () => {
      const state = stateRef.current;
      
      if (state.score >= 37) {
        if (!state.hasSpawnedGoal) {
          state.pipes.push({ x: canvas.width, isGoal: true });
          state.hasSpawnedGoal = true;
        }
        return;
      }

      const minHeight = 60;
      const maxHeight = canvas.height - pipeGap - minHeight;
      const topHeight = Math.floor(Math.random() * (maxHeight - minHeight + 1)) + minHeight;
      stateRef.current.pipes.push({ x: canvas.width, topHeight, passed: false, isGoal: false });
    };

    const updateAndRender = () => {
      const state = stateRef.current;
      state.frameCounter++;
      const currentScore = state.score;

      ctx.imageSmoothingEnabled = false;
      ctx.setTransform(1, 0, 0, 1, 0, 0);

      let bgColor = '#FFFFFF';
      let mainColor = '#000000';
      
      if (currentScore >= 10 && currentScore < 20) {
        bgColor = '#000000';
        mainColor = '#FFFFFF';
      } else if (currentScore >= 20 && currentScore < 30) {
        bgColor = '#FFFFFF';
        mainColor = '#000000';
      } else if (currentScore >= 30 && currentScore < 39) {
        if (currentScore % 2 === 1) {
          bgColor = '#00FF66'; 
          mainColor = '#FFFFFF'; 
        } else {
          bgColor = '#FF5500'; 
          mainColor = '#FFFFFF'; 
        }

        if (state.flashTimer > 0) {
          state.flashTimer--;
          bgColor = state.frameCounter % 4 < 2 ? '#FF5500' : '#00FF66';
          mainColor = '#FFFFFF';
        }
      } else if (currentScore >= 39) {
        bgColor = '#FFFFFF';
        mainColor = '#000000';
      }

      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      if (state.gameState === 'PLAYING') {
        if (state.isCharging && !state.overshot) {
          state.charge += 0.45;
          if (state.charge >= maxCharge) {
            state.overshot = true;
            state.bunny.vy = -maxCharge * 1.35; 
            state.charge = 0;
            state.isCharging = false;
          }
        } else {
          state.bunny.vy += gravity;
          state.bunny.y += state.bunny.vy;
        }

        if (state.bunny.y < -100 || state.bunny.y + state.bunny.height > canvas.height + 100) {
          triggerGameOver();
        }

        if (state.frameCounter % 95 === 0) spawnPipe();

        state.pipes.forEach((pipe) => {
          pipe.x -= pipeSpeed;
          
          if (pipe.isGoal) {
            if (state.goalX === null) state.goalX = pipe.x;
            state.goalX -= pipeSpeed;

            const rectWidth = Math.max(pipeWidth, canvas.width + 500 - pipe.x);
            ctx.fillStyle = '#00FF66';
            ctx.fillRect(pipe.x, 0, rectWidth, canvas.height);
            
            ctx.strokeStyle = '#228B22';
            ctx.lineWidth = 6;
            ctx.strokeRect(pipe.x, -5, rectWidth, canvas.height + 10);
            
            ctx.fillStyle = '#006622';
            const xBase = Math.floor(pipe.x);
            
            for (let xOffset = 10; xOffset < canvas.width + 600; xOffset += 35) {
              for (let yOffset = 20; yOffset < canvas.height; yOffset += 40) {
                ctx.font = '16px monospace';
                
                const coordSeed = Math.floor((xOffset * 31) + (yOffset * 17));
                
                const jitterX = (coordSeed % 23) - 11;
                const jitterY = (coordSeed % 19) - 9;
                
                let grassChar = ' ';
                const charPick = coordSeed % 4;
                if (charPick === 0) grassChar = 'W';
                else if (charPick === 1) grassChar = 'V';
                else if (charPick === 2) grassChar = 'w';
                
                if (grassChar !== ' ') {
                  ctx.fillText(grassChar, xBase + xOffset + jitterX, yOffset + jitterY);
                }
              }
            }

            const b = state.bunny;
            if (b.x + b.width >= pipe.x + 2) {
              state.score = 40;
              setScore(40);
              triggerWin();
            }

          } else {
            if (currentScore >= 20) {
              ctx.fillStyle = mainColor;
              ctx.font = '16px monospace';
              
              for (let y = 0; y < pipe.topHeight; y += 18) {
                for (let x = 0; x < pipeWidth; x += 14) {
                  const char = symbols[(Math.floor(state.frameCounter / 10) + x + y) % symbols.length];
                  ctx.fillText(char, pipe.x + x, y + 14);
                }
              }
              
              const bottomY = pipe.topHeight + pipeGap;
              for (let y = bottomY; y < canvas.height; y += 18) {
                for (let x = 0; x < pipeWidth; x += 14) {
                  const char = symbols[(Math.floor(state.frameCounter / 10) + x + y) % symbols.length];
                  ctx.fillText(char, pipe.x + x, y + 14);
                }
              }

            } else {
              ctx.lineWidth = 4;
              ctx.strokeStyle = mainColor;
              ctx.fillStyle = bgColor;
              
              ctx.fillRect(pipe.x, 0, pipeWidth, pipe.topHeight);
              ctx.strokeRect(pipe.x, -5, pipeWidth, pipe.topHeight + 5);
              
              const bottomY = pipe.topHeight + pipeGap;
              ctx.fillRect(pipe.x, bottomY, pipeWidth, canvas.height - bottomY);
              ctx.strokeRect(pipe.x, bottomY, pipeWidth, canvas.height - bottomY + 5);
            }

            if (!pipe.passed && pipe.x + pipeWidth < state.bunny.x) {
              pipe.passed = true;
              state.score += 1;
              setScore(state.score);
              
              if (state.score >= 30 && state.score < 39) {
                state.flashTimer = 20; 
              }
            }

            const b = state.bunny;
            const bottomY = pipe.topHeight + pipeGap;
            if (
              b.x < pipe.x + pipeWidth &&
              b.x + b.width > pipe.x &&
              (b.y < pipe.topHeight || b.y + b.height > bottomY)
            ) {
              triggerGameOver();
            }
          }
        });

        state.pipes = state.pipes.filter(p => p.x > -pipeWidth || p.isGoal);

        if (state.overshot && state.bunny.vy > 0) {
          state.overshot = false;
        }

        if (state.isCharging || state.overshot) {
          const barW = 140, barH = 14, barX = canvas.width / 2 - barW / 2, barY = canvas.height - 45;
          ctx.strokeStyle = mainColor;
          ctx.lineWidth = 3;
          ctx.strokeRect(barX, barY, barW, barH);

          if (state.overshot) {
            ctx.fillStyle = mainColor;
            ctx.fillRect(barX, barY, barW, barH);
          } else {
            const fillW = (state.charge / maxCharge) * barW;
            ctx.fillStyle = mainColor;
            ctx.fillRect(barX, barY, fillW, barH);
          }
        }

        ctx.fillStyle = (currentScore >= 10 && currentScore < 20) || (currentScore >= 30 && currentScore < 39) ? '#FFFFFF' : '#000000';
        ctx.font = 'bold 32px "Press Start 2P", monospace';
        ctx.textAlign = 'center';
        ctx.fillText(currentScore.toString(), canvas.width / 2, canvas.height - 75);
      }

      const b = state.bunny;
      const activeSprite = state.isCharging ? imgAction.current : imgIdle.current;

      if (spritesLoaded.current) {
        ctx.save();
        
        if (currentScore >= 10 && currentScore < 20) {
          ctx.filter = 'invert(100%)';
        } else if (currentScore >= 30 && currentScore < 39) {
          ctx.filter = 'brightness(0) invert(100%)';
        }

        const offsetX = (visualSize - b.width) / 2;
        const offsetY = (visualSize - b.height) / 2;

        ctx.drawImage(
          activeSprite, 
          b.x - offsetX, 
          b.y - offsetY, 
          visualSize, 
          visualSize
        );
        ctx.restore();
      }

      requestRef.current = requestAnimationFrame(updateAndRender);
    };

    const triggerGameOver = () => {
      cancelAnimationFrame(requestRef.current);
      playSound('death');
      setGameState('GAMEOVER');
      
      const currentHigh = parseInt(getCookie('flappy_ears_highscore') || '0', 10);
      if (stateRef.current.score > currentHigh) {
        setCookie('flappy_ears_highscore', stateRef.current.score.toString());
        setHighScore(stateRef.current.score);
      }
    };

    const triggerWin = () => {
      cancelAnimationFrame(requestRef.current);
      playSound('win');
      setGameState('WIN');
      
      const currentHigh = parseInt(getCookie('flappy_ears_highscore') || '0', 10);
      if (40 > currentHigh) {
        setCookie('flappy_ears_highscore', '40');
        setHighScore(40);
      }
    };

    const handleInputStart = (e) => {
      if (e.target && (e.target.tagName === 'BUTTON' || e.target.closest('.btn-3d') || e.target.closest('.btn-victory-container'))) {
        return;
      }
      if (e.type === 'touchstart' && e.cancelable) {
        e.preventDefault(); 
      }
      if (e.code && e.code !== 'Space') return;
      if (stateRef.current.gameState === 'PLAYING' && !stateRef.current.overshot) {
        stateRef.current.isCharging = true;
      }
    };

    const handleInputEnd = (e) => {
      if (e.type === 'touchend' && e.cancelable) {
        e.preventDefault();
      }
      if (e.code && e.code !== 'Space') return;
      const state = stateRef.current;
      if (state.gameState === 'PLAYING' && state.isCharging && !state.overshot) {
        state.bunny.vy = -state.charge;
        state.charge = 0;
        state.isCharging = false;
      }
    };

    window.addEventListener('keydown', handleInputStart);
    window.addEventListener('keyup', handleInputEnd);
    window.addEventListener('mousedown', handleInputStart);
    window.addEventListener('mouseup', handleInputEnd);
    
    window.addEventListener('touchstart', handleInputStart, { passive: false });
    window.addEventListener('touchend', handleInputEnd, { passive: false });

    requestRef.current = requestAnimationFrame(updateAndRender);

    return () => {
      cancelAnimationFrame(requestRef.current);
      window.removeEventListener('keydown', handleInputStart);
      window.removeEventListener('keyup', handleInputEnd);
      window.removeEventListener('mousedown', handleInputStart);
      window.removeEventListener('mouseup', handleInputEnd);
      window.removeEventListener('touchstart', handleInputStart);
      window.removeEventListener('touchend', handleInputEnd);
    };
  }, [gameState]);

  useEffect(() => {
    const handleGlobalInputStart = (e) => {
      if (e.key === 'x' || e.key === 'X') {
        if (stateRef.current.gameState === 'PLAYING') {
          stateRef.current.score = Math.min(39, stateRef.current.score + 5);
          setScore(stateRef.current.score);
          return;
        }
      }
    };

    window.addEventListener('keydown', handleGlobalInputStart);
    return () => {
      window.removeEventListener('keydown', handleGlobalInputStart);
    };
  }, []);

  const handleExecuteShatter = (e) => {
    if (e) {
      e.stopPropagation();
      if (e.cancelable) e.preventDefault();
    }
    playSound('break');
    setIsShatteredActive(true);
    
    const cols = 6;
    const rows = 6;
    const blocks = [];
    
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        blocks.push({
          id: `${r}-${c}`,
          top: `${(r * 100) / rows}vh`,
          left: `${(c * 100) / cols}vw`,
          width: `${100 / cols}vw`,
          height: `${100 / rows}vh`,
          vx: (Math.random() - 0.5) * 15,
          vy: -Math.random() * 20 - 5,
          rotation: 0,
          vRot: (Math.random() - 0.5) * 12,
          currX: 0,
          currY: 0
        });
      }
    }
    setShatteredBlocks(blocks);
  };

  useEffect(() => {
    if (!isShatteredActive || shatteredBlocks.length === 0) return;
    
    let localBlocks = [...shatteredBlocks];
    const gravity = 0.8;

    const runPhysicsLoop = () => {
      localBlocks = localBlocks.map(block => {
        const nextVy = block.vy + gravity;
        const nextX = block.currX + block.vx;
        const nextY = block.currY + block.vy;
        const nextRot = block.rotation + block.vRot;

        return {
          ...block,
          vy: nextVy,
          currX: nextX,
          currY: nextY,
          rotation: nextRot
        };
      });

      setShatteredBlocks(localBlocks);
      if (localBlocks.some(b => b.currY < window.innerHeight + 200)) {
        requestAnimationFrame(runPhysicsLoop);
      }
    };

    const animFrame = requestAnimationFrame(runPhysicsLoop);
    return () => cancelAnimationFrame(animFrame);
  }, [isShatteredActive]);

  const handleResetFromWin = (e) => {
    if (e) {
      e.stopPropagation();
      if (e.cancelable) e.preventDefault();
    }
    setIsShatteredActive(false);
    setShatteredBlocks([]);
    setScore(0);
    setGameState('HOME');
  };

  const getDynamicStageClass = () => {
    if (score >= 39) return 'stage-victory-unlocked'; 
    if (score >= 30 && score < 39) return 'stage-orange-glitch';
    if (score >= 20) return 'stage-letters';
    if (score >= 10) return 'stage-inverted';
    return 'stage-normal';
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap');
        .pixel-font { font-family: 'Press Start 2P', monospace; }
        
        canvas {
          image-rendering: -moz-crisp-edges;
          image-rendering: -webkit-crisp-edges;
          image-rendering: pixelated;
          image-rendering: crisp-edges;
          max-width: 100%;
          max-height: 75vh;
          object-fit: contain;
        }

        .btn-3d {
          position: relative;
          background: #ffffff;
          border: 4px solid #000000;
          color: #000000;
          padding: 16px 36px;
          font-size: 14px;
          font-weight: bold;
          cursor: pointer;
          box-shadow: 0px 8px 0px #000000;
          top: 0px;
          z-index: 100;
          touch-action: manipulation;
        }

        .btn-3d.quick-press {
          animation: fastSlam 0.06s steps(2) forwards;
        }

        @keyframes fastSlam {
          0% { top: 0px; box-shadow: 0px 8px 0px #000000; }
          100% { top: 8px; box-shadow: 0px 0px 0px #000000; }
        }

        .rumble-viewport {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100vw;
          height: 100vh;
          overflow: hidden;
          background-color: #FFFFFF;
          padding: 20px;
          box-sizing: border-box;
          user-select: none;
          touch-action: none;
        }

        @media (max-width: 768px) {
          .rumble-viewport {
            justify-content: center;
          }
          .rumble-viewport-playing {
            justify-content: flex-start !important;
            padding-top: 6vh !important;
          }
        }

        .rumble-viewport.active-rumble {
          animation: luxuryRumble 0.15s linear infinite, luxuryStrobe 0.1s linear infinite;
        }

        .marquee-bg-layer {
          position: absolute;
          top: 0; left: 0; width: 100vw; height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 2vh 0;
          opacity: 0.05;
          z-index: 1;
          pointer-events: none;
          font-weight: bold;
          font-size: 4.5vh;
          white-space: nowrap;
          overflow: hidden;
        }

        .stage-normal .marquee-bg-layer { color: #000000; }
        .stage-inverted .marquee-bg-layer { color: #FFFFFF; opacity: 0.08; }
        .stage-letters .marquee-bg-layer { color: #000000; }
        
        .stage-orange-glitch .marquee-bg-layer { 
          color: #FF5500; 
          opacity: 0.35; 
        }

        .stage-victory-unlocked .marquee-bg-layer { 
          color: #00FF66; 
          opacity: 0.35;
          text-shadow: 3px 3px #FF5500, -3px -3px #FFFFFF;
        }

        .marquee-row { 
          display: inline-block;
          white-space: nowrap;
          width: max-content;
        }
        
        .move-left-to-right { animation: loopLeftToRight 160s linear infinite; }
        .move-right-to-left { animation: loopRightToLeft 140s linear infinite; }
        .row-fast { animation-duration: 100s; }
        .row-slow { animation-duration: 220s; }

        @keyframes loopLeftToRight {
          0% { transform: translate3d(-50%, 0, 0); }
          100% { transform: translate3d(0%, 0, 0); }
        }
        @keyframes loopRightToLeft {
          0% { transform: translate3d(0%, 0, 0); }
          100% { transform: translate3d(-50%, 0, 0); }
        }

        .premium-victory-card {
          text-align: center;
          position: absolute;
          z-index: 150;
          width: 90vw;
          max-width: 580px;
          background-color: #FFFFFF;
          color: #000000;
          padding: 30px 20px;
          border: 8px solid #000000;
          box-shadow: 12px 12px 0px #000000;
          animation: entryBounce 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }

        .victory-decorative-stripe {
          border-top: 6px dotted #000000;
          border-bottom: 6px dotted #000000;
          padding: 16px 0;
          margin: 18px 0;
          background: rgba(0, 0, 0, 0.02);
        }

        .victory-cross-stitch {
          font-size: 8px;
          letter-spacing: 4px;
          color: #888888;
          margin: 10px 0;
        }

        .victory-badge {
          display: inline-block;
          border: 2px dashed #000000;
          padding: 6px 12px;
          font-size: 11px;
          margin-bottom: 20px;
          background: #FAFAFA;
        }

        /* Adjusted 3D container borders to form a clean, low-profile shadow shade */
        .btn-victory-container {
          display: inline-block;
          position: relative;
          background: #FFFFFF;
          border-right: 4px solid #000000;
          border-bottom: 4px solid #000000;
          padding: 0;
          cursor: pointer;
        }

        .btn-victory-break {
          background: #000000;
          border: 4px solid #000000;
          color: #FFFFFF;
          padding: 18px 44px;
          font-size: 16px;
          font-weight: bold;
          position: relative;
          top: -4px;
          left: -4px;
          z-index: 20;
          display: block;
          user-select: none;
          pointer-events: none;
        }

        /* Adjusted depth tracking to align with the streamlined shade footprint */
        .btn-victory-container::before {
          content: '';
          position: absolute;
          top: 0px;
          left: 0px;
          right: -4px;
          bottom: -4px;
          background: #FFFFFF;
          border: 4px solid #000000;
          z-index: 10;
        }

        .btn-victory-container:active .btn-victory-break {
          top: 0px;
          left: 0px;
        }
        
        .btn-victory-container:active {
          border-right: 0px solid #000000;
          border-bottom: 0px solid #000000;
          transform: translate3d(4px, 4px, 0px);
          margin-bottom: 4px;
          margin-right: 4px;
        }

        @keyframes entryBounce {
          0% { transform: scale(0.3); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }

        .shatter-overlay-container {
          position: fixed;
          top: 0; left: 0; width: 100vw; height: 100vh;
          pointer-events: none;
          z-index: 99999;
          background: transparent;
        }

        .shatter-shard {
          position: absolute;
          background: #000000;
          border: 1px solid #FFFFFF;
          box-sizing: border-box;
          transform-origin: center center;
        }

        @keyframes luxuryRumble {
          0% { transform: translate(2px, 1px) rotate(0deg); }
          20% { transform: translate(-3px, 0px) rotate(1deg); }
          40% { transform: translate(1px, -1px) rotate(1deg); }
          60% { transform: translate(-3px, 1px) rotate(0deg); }
          80% { transform: translate(-1px, -1px) rotate(1deg); }
          100% { transform: translate(1px, -2px) rotate(-1deg); }
        }

        @keyframes luxuryStrobe {
          0%, 100% { filter: invert(0%); }
          50% { filter: invert(100%); }
        }
      `}</style>

      <div 
        className={`pixel-font ${luxuriousCinemaActive ? 'active-rumble' : ''} rumble-viewport ${gameState === 'PLAYING' ? 'rumble-viewport-playing' : ''} ${getDynamicStageClass()}`}
        style={{ opacity: isShatteredActive ? 0 : 1, transition: 'opacity 0.1s ease' }}
      >
        
        <div className="marquee-bg-layer">
          <div className="marquee-row move-left-to-right row-fast">FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS</div>
          <div className="marquee-row move-right-to-left">(\\__/) (\\__/) (\\__/) (\\__/) (\\__/) (\\__/) (\\__/) (\\__/) (\\__/) (\\__/) (\\__/) (\\__/)</div>
          <div className="marquee-row move-left-to-right row-slow">FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS</div>
          <div className="marquee-row move-right-to-left row-fast">FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS</div>
          <div className="marquee-row move-left-to-right"> (='.'=) (='.'=) (='.'=) (='.'=) (='.'=) (='.'=) (='.'=) (='.'=) (='.'=) (='.'=) (='.'=) (='.'=)</div>
          <div className="marquee-row move-right-to-left row-slow">FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS FLAPPY EARS</div>
        </div>

        {gameState === 'HOME' && (
          <div style={{ textAlign: 'center', zIndex: 10 }}>
            <h1 style={{ fontSize: '2rem', marginBottom: '25px', color: '#000000' }}>FLAPPY EARS</h1>
            <p style={{ fontSize: '0.8rem', marginBottom: '45px', color: '#666' }}>HIGH SCORE: {highScore}</p>
            <button 
              className={`btn-3d ${buttonPressedState ? 'quick-press' : ''}`} 
              onClick={handlePlayClick}
              onTouchStart={(e) => handlePlayClick(e)}
              disabled={buttonPressedState || luxuriousCinemaActive}
            >
              PLAY
            </button>
          </div>
        )}

        {gameState === 'GAMEOVER' && (
          <div style={{
            textAlign: 'center', position: 'absolute', zIndex: 10,
            backgroundColor: '#FFFFFF', color: '#000000', padding: '35px', border: '4px solid #000'
          }}>
            <h2 style={{ fontSize: '1.4rem', margin: '0 0 20px 0' }}>GAME OVER</h2>
            <p style={{ fontSize: '0.8rem', marginBottom: '12px' }}>SCORE: {score}</p>
            <p style={{ fontSize: '0.8rem', marginBottom: '35px' }}>BEST: {highScore}</p>
            <button 
              className={`btn-3d ${buttonPressedState ? 'quick-press' : ''}`} 
              onClick={handlePlayClick}
              onTouchStart={(e) => handlePlayClick(e)}
              disabled={buttonPressedState || luxuriousCinemaActive}
            >
              TRY AGAIN
            </button>
          </div>
        )}

        {gameState === 'WIN' && (
          <div className="premium-victory-card">
            <div style={{ fontSize: '0.6rem', color: '#888', letterSpacing: '2px', marginBottom: '5px' }}>- ACHIEVEMENT UNLOCKED -</div>
            <h2 style={{ fontSize: '1.5rem', margin: '0 0 5px 0', letterSpacing: '-1px', color: '#000000' }}>REACHED THE FINISHLINE</h2>
            
            <div className="victory-cross-stitch">X-X-X-X-X-X-X-X-X-X-X-X-X-X-X-X-X-X</div>

            <div className="victory-decorative-stripe">
              <p style={{ fontSize: '0.8rem', color: '#000000', margin: '0 0 8px 0', fontWeight: 'bold' }}>Bunny safely reached his home destination.</p>
              <p style={{ fontSize: '0.55rem', color: '#666666', letterSpacing: '1px' }}>MADE FOR M. L. D.</p>
            </div>

            <div className="victory-badge">FINAL SCORE: 40 / 40</div>

            <div style={{ margin: '0 auto 25px auto', maxWidth: '92%', lineHeight: '1.7' }}>
              <p style={{ fontSize: '0.85rem', color: '#000000', margin: '0 0 4px 0', fontWeight: 'bold', fontStyle: 'italic', letterSpacing: '1px' }}>
                *crunch-crunch-crunch!*
              </p>
              <p style={{ fontSize: '0.75rem', color: '#333333', margin: '0', fontWeight: 'bold' }}>
                You hear happy carrot munching noises...
              </p>
            </div>

            <div className="victory-cross-stitch" style={{ marginBottom: '25px' }}>X-X-X-X-X-X-X-X-X-X-X-X-X-X-X-X-X-X</div>
            
            <div 
              className="btn-victory-container"
              onClick={handleExecuteShatter}
              onTouchStart={(e) => handleExecuteShatter(e)}
            >
              <button className="btn-victory-break">
                ???
              </button>
            </div>
          </div>
        )}

        <canvas 
          ref={canvasRef} 
          style={{ 
            border: '4px solid #000000', 
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            display: (gameState === 'HOME' || gameState === 'WIN' || luxuriousCinemaActive) ? 'none' : 'block',
            zIndex: 5
          }} 
        />
      </div>

      {isShatteredActive && (
        <div className="shatter-overlay-container">
          {shatteredBlocks.map(block => (
            <div
              key={block.id}
              className="shatter-shard"
              style={{
                top: block.top,
                left: block.left,
                width: block.width,
                height: block.height,
                transform: `translate3d(${block.currX}px, ${block.currY}px, 0px) rotate(${block.rotation}deg)`
              }}
            />
          ))}

          <div style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            pointerEvents: 'auto', zIndex: 999999, textAlign: 'center'
          }}>
            <h2 className="pixel-font" style={{ color: '#FFFFFF', fontSize: '1.5rem', marginBottom: '30px', textShadow: '3px 3px #000' }}>
              SYSTEM COLLAPSE
            </h2>
            <button 
              className="btn-3d pixel-font" 
              onClick={handleResetFromWin}
              onTouchStart={(e) => handleResetFromWin(e)}
            >
              REBOOT GAME
            </button>
          </div>
        </div>
      )}
    </>
  );
}